// The effects director: each frame it looks at what the playhead crossed, picks what to show within a budget,
// sets places burning while long events last, and asks for sound, camera shake and flashes.

import type { Conflicts } from "../lib/data";
import { audio } from "./audio";
import {
  Bucket, burningAt, clamp, countFocus, crossed, ERA_BLADE, ERA_RIFLE, ERA_SHELL, eraTime, FULL, FX_BATTLE, FX_BOMBING,
  FX_MEMORIAL, FX_NAVAL, FX_NONE, FX_NUKE, FX_SIEGE, FX_UPRISING, hearing, POP, triage, type FxEvents,
} from "./logic";
import { useFx } from "./state";
import { Anchors, blast, bombing, burn, melee, memorial, naval, nuke, Particles, pop, uprising, volleyLine, type View } from "./particles";

const DAY = 1 / 365.25;
const MOURN = 15; // seconds after an atrocity is marked during which no fighting is heard near it…
const MOURN_KM = 60; // …within this distance
const MOURN_PX = 48; // …or this close on screen (zoomed out, 60 km is a few pixels)

/** Great-circle distance in km. */
function km(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const r = Math.PI / 180, a = Math.sin(((lat2 - lat1) * r) / 2) ** 2 +
    Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(((lon2 - lon1) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** An event's identity across frames (and chunks): its source, id and effect time. */
const keyOf = (ev: FxEvents, k: number) => `${ev.src}:${ev.id ? ev.id[k] : k}:${ev.t[k]}`;

export interface Frame {
  now: number; // wall time, seconds
  dt: number;
  t: number;
  tPrev: number;
  span: number;
  playing: boolean;
  conflicts: Conflicts;
  focus: Uint8Array | null;
  kinds: boolean[];
  events: boolean; // the "battles & events" layer is on
  replay: { t0: number; t1: number; holding: boolean } | null;
  view: View;
  project: (lon: number, lat: number) => [number, number];
  centerLon: number;
  motion: boolean; // shake and flashes allowed
  quality: number; // 0.3–1: fewer particles on slow devices
  sound: boolean;
}

interface Site {
  key: string;
  anchor: number;
  m: number;
  era: number;
  t0: number;
  t1: number;
  long: boolean; // burns while the event lasts (data time), not only for a while after it starts
  minUntil: number; // wall time it burns at least until
  intensity: number;
  acc: Float32Array;
  focus: boolean; // part of the replayed war
}

interface Pending { at: number; run: () => void }

export class Director {
  private sources: FxEvents[] = [];
  private sites = new Map<string, Site>();
  private full = new Bucket(5, 3.2);
  private pops = new Bucket(24, 22);
  private pending: Pending[] = [];
  private lastReconcile = -1;
  private lastStats = -1;
  private lastFlash = -10;
  private lastDistant = 0;
  private fired = new Map<string, number>(); // event key → clock when it got a full effect (blast or chapter)
  private mourning: { lon: number; lat: number; until: number }[] = []; // atrocities just marked: no battle heard near
  private proj: Frame["project"] = () => [NaN, NaN]; // the last frame's projection (for distances on screen)
  private centerLon = 0;
  private clock = 0; // simulation time (s): the frames' clamped steps, so a hidden tab does not age anything
  trauma = 0;
  flash = 0; // 0–1, the white-out over the screen
  level = 0; // fighting on screen, 0–1 (for the ambience)
  fire = 0; // flames near the middle of the screen, 0–1 (for the ambience)

  constructor(readonly P: Particles, readonly A: Anchors) {}

  setSources(s: FxEvents[]) {
    this.sources = s;
    this.lastReconcile = -1;
  }

  clear() {
    this.P.clear();
    this.sites.clear();
    this.mourning = [];
    this.pending = [];
    this.trauma = 0;
    this.flash = 0;
  }

  /** Visible root-kind filter and focus, as the map's own dots use them. */
  private filter(ev: FxEvents, k: number, f: Frame): { hidden: boolean; dim: boolean } {
    const ci = ev.c[k];
    const c = f.conflicts;
    if (ci >= 0 && ci < c.n) {
      const root = c.r[ci] >= 0 ? c.r[ci] : ci;
      if (!f.kinds[c.kind[root]]) return { hidden: true, dim: false };
    }
    return { hidden: false, dim: !!f.focus && (ci < 0 || !f.focus[ci]) };
  }

  frame(f: Frame): void {
    this.clock = f.now;
    this.proj = f.project;
    this.centerLon = f.centerLon;
    this.full.refill(f.dt);
    this.pops.refill(f.dt);
    for (let i = this.pending.length - 1; i >= 0; i--) {
      if (f.now >= this.pending[i].at) {
        const p = this.pending[i];
        this.pending.splice(i, 1);
        p.run();
      }
    }
    const dT = f.t - f.tPrev;
    const jumpLimit = Math.max(f.span * 0.03, 1.5 * DAY);
    const jumped = dT < 0 || dT > jumpLimit;
    if (f.events && dT > 0 && !jumped) this.ignite(f);
    if (jumped) {
      // the playhead was moved: what has not started yet stops burning; long events at the new moment light up;
      // anything may be shown again
      for (const s of this.sites.values()) if (s.t0 > f.t || (s.long && s.t1 <= f.t)) { s.long = false; s.minUntil = 0; }
      this.lastReconcile = -1;
      this.fired.clear();
    }
    if (f.events && (this.lastReconcile < 0 || f.now - this.lastReconcile > 0.8)) {
      this.lastReconcile = f.now;
      this.reconcile(f);
    }
    if (!f.events && this.sites.size) this.sites.clear();
    this.tend(f);
    this.trauma = Math.max(0, this.trauma - f.dt * 0.95);
    this.flash *= Math.exp(-f.dt * 3.2);
    if (this.flash < 0.002) this.flash = 0;
    if (f.now - this.lastStats > 0.25) {
      this.lastStats = f.now;
      let burning = 0;
      for (const s of this.sites.values()) if (s.intensity > 0.2 && this.A.on[s.anchor]) burning++;
      const battles = f.replay && f.focus ? countFocus(this.sources, f.focus, f.replay.t0 - DAY, f.t) : 0;
      useFx.getState().setStats({ battles, burning });
    }
  }

  /** What the playhead crossed this frame. */
  private ignite(f: Frame) {
    const cands: { ev: FxEvents; k: number; m: number; x: number; y: number; on: boolean; dim: boolean }[] = [];
    for (const ev of this.sources) {
      const [lo, hi] = crossed(ev, f.tPrev, f.t);
      for (let k = lo; k < hi; k++) {
        if (ev.fx[k] === FX_NONE || this.fired.has(keyOf(ev, k))) continue; // a chapter already showed it
        const { hidden, dim } = this.filter(ev, k, f);
        if (hidden) continue;
        const lon = ev.lon[k] + 360 * Math.round((f.centerLon - ev.lon[k]) / 360);
        const [x, y] = f.project(lon, ev.lat[k]);
        const on = x > -40 && x < f.view.w + 40 && y > -40 && y < f.view.h + 40;
        cands.push({ ev, k, m: dim ? ev.mag[k] * 0.35 : ev.mag[k], x, y, on, dim });
      }
    }
    if (!cands.length) return;
    for (const c of cands) { // atrocities, on screen or just off it, small or large: no fighting heard near them for a while
      if (c.ev.fx[c.k] === FX_MEMORIAL) this.mourn(c.ev.lon[c.k], c.ev.lat[c.k], f.now);
    }
    cands.sort((a, b) => b.m - a.m);
    const vis = cands.filter((c) => c.on);
    const plan = triage(vis.map((c) => c.m), this.full, this.pops, f.quality < 0.5 ? 2 : 3);
    vis.forEach((c, j) => {
      // a battle where an atrocity was just marked: a small silent flash, not a blast and gunfire over the memorial
      const hushed = plan[j] === FULL && c.ev.fx[c.k] !== FX_MEMORIAL && this.mourned(c.ev.lon[c.k], c.ev.lat[c.k], c.x, c.y);
      if (plan[j] === FULL && !hushed) this.effect(c.ev, c.k, c.m, f, c.x, c.y, true);
      else if (plan[j] === POP || hushed) {
        const a = this.A.add(c.ev.lon[c.k], c.ev.lat[c.k], f.now + 3);
        pop(this.P, a, c.m, c.ev.fx[c.k] === FX_MEMORIAL);
      }
    });
    // a big battle elsewhere, heard far away (horns and battle cries before gunpowder, guns after)
    const far = cands.find((c) => !c.on && !c.dim && c.m >= 0.5 && c.ev.fx[c.k] !== FX_MEMORIAL);
    if (far && f.sound && f.now - this.lastDistant > 1.2) {
      this.lastDistant = f.now;
      audio.distant(eraTime(far.ev.t[far.k], far.ev.era[far.k]), far.m, far.x < f.view.w / 2 ? -0.7 : 0.7);
    }
  }

  /** A full effect for event k: picture, sound, shake; long or large events keep burning. */
  effect(ev: FxEvents, k: number, m: number, f: Frame, x: number, y: number, withSound: boolean, hero = false) {
    const fx = ev.fx[k], era = ev.era[k], q = f.quality;
    const key = keyOf(ev, k), place = hearing(x, y, f.view.w, f.view.h);
    if (hero && (this.fired.get(key) ?? -1e9) > f.now - 4) { // it went off on screen a moment ago
      if (fx === FX_MEMORIAL && withSound && f.sound) audio.event(fx, ev.t[k], m, place, true); // the chapter's bell rings
      return;
    }
    if (this.fired.size > 4000) this.fired.clear();
    this.fired.set(key, f.now);
    const a = this.A.add(ev.lon[k], ev.lat[k], f.now + (fx === FX_NUKE ? 45 : 32));
    if (a < 0) return;
    const sound = withSound && f.sound;
    let shake = 0;
    if (sound) audio.event(fx, eraTime(ev.t[k], era), m, place, hero); // as its period (the picture's) heard it; a bell for atrocities
    if (fx === FX_MEMORIAL) this.mourn(ev.lon[k], ev.lat[k], f.now); // even where a battle still burns, no fighting heard
    switch (fx) {
      case FX_NUKE:
        nuke(this.P, a, q);
        shake = 1;
        if (f.motion) this.flash = 1;
        break;
      case FX_MEMORIAL:
        memorial(this.P, a, m, q);
        break;
      case FX_NAVAL:
        naval(this.P, a, m, q, era);
        shake = era >= ERA_SHELL ? 0.35 * m : 0.1 * m;
        break;
      case FX_BOMBING:
        bombing(this.P, a, m, q);
        shake = 0.5 * m;
        break;
      case FX_UPRISING:
        uprising(this.P, a, m, q, era);
        shake = era >= ERA_SHELL ? 0.15 * m : 0;
        break;
      case FX_SIEGE:
      case FX_BATTLE:
      default:
        if (era === ERA_BLADE) {
          melee(this.P, a, m, q);
          shake = 0.12 * m;
        } else if (era <= ERA_RIFLE) {
          // lines of muskets and guns in their smoke; big battles and sieges add exploding shells
          volleyLine(this.P, a, m, q, era);
          if (m > 0.5 || fx === FX_SIEGE) blast(this.P, a, m * 0.6, q, { era, delay: 0.5, scorch: false });
          shake = 0.25 * m;
        } else {
          blast(this.P, a, m, q, { era });
          shake = 0.55 * m * m + 0.08;
        }
        if (fx === FX_SIEGE) {
          // a bombardment: shells or stones landing all over the place for a few seconds
          for (let j = 0; j < 2 + Math.round(3 * m); j++) {
            const d = 0.4 + j * 0.45, ox = (Math.random() - 0.5) * 40, oy = (Math.random() - 0.5) * 40;
            if (era === ERA_BLADE) this.later(f.now + d, () => melee(this.P, a, m * 0.4, q * 0.5));
            else this.later(f.now + d, () => blast(this.P, a, m * 0.4, q * 0.5, { x: ox, y: oy, era, scorch: j % 2 === 0 }));
          }
        }
    }
    if (f.motion && shake > 0) this.trauma = clamp(this.trauma + shake * (1 - place.dist) * (hero ? 1.4 : 1), 0, 1);
    if (f.motion && fx !== FX_NUKE && m >= 0.82 && era >= ERA_SHELL && fx !== FX_MEMORIAL && f.now - this.lastFlash > 1.6) {
      this.lastFlash = f.now;
      this.flash = Math.max(this.flash, hero ? 0.22 : 0.14); // well under any photosensitivity threshold, never repeated quickly
    }
    // big or long events burn on
    if (fx !== FX_MEMORIAL && (ev.burn[k] || m >= 0.3 || fx === FX_SIEGE || fx === FX_NUKE || hero)) {
      const old = this.sites.get(key);
      const minUntil = f.now + (fx === FX_NUKE ? 9 : 2.5 + 5 * m) + (hero ? 3 : 0);
      if (old) old.minUntil = Math.max(old.minUntil, minUntil);
      else {
        this.sites.set(key, { key, anchor: a, m: fx === FX_NUKE ? 1 : m, era, t0: ev.t[k], t1: ev.t1[k], long: !!ev.burn[k], minUntil,
          intensity: 0.4, acc: new Float32Array(5), focus: !f.focus || (ev.c[k] >= 0 && !!f.focus[ev.c[k]]) });
      }
    }
  }

  private later(at: number, run: () => void) {
    if (this.pending.length < 64) this.pending.push({ at, run });
  }

  /** Long events going on at this moment burn (no blast): sieges under way when one arrives at a date. */
  private reconcile(f: Frame) {
    const want: { ev: FxEvents; k: number; m: number }[] = [];
    for (const ev of this.sources) {
      if (ev.src !== "named") continue;
      for (const k of burningAt(ev, f.t)) {
        const { hidden, dim } = this.filter(ev, k, f);
        if (hidden || (dim && f.replay)) continue;
        const lon = ev.lon[k] + 360 * Math.round((f.centerLon - ev.lon[k]) / 360);
        const [x, y] = f.project(lon, ev.lat[k]);
        if (x < -60 || x > f.view.w + 60 || y < -60 || y > f.view.h + 60) continue;
        want.push({ ev, k, m: dim ? ev.mag[k] * 0.5 : ev.mag[k] });
      }
    }
    want.sort((a, b) => b.m - a.m);
    const max = f.quality < 0.5 ? 5 : 10;
    for (const w of want.slice(0, max)) {
      const { ev, k } = w;
      const key = keyOf(ev, k);
      if (this.sites.has(key)) continue;
      const a = this.A.add(ev.lon[k], ev.lat[k], f.now + 30);
      if (a < 0) break;
      this.sites.set(key, { key, anchor: a, m: w.m, era: ev.era[k], t0: ev.t[k], t1: ev.t1[k], long: true, minUntil: 0, intensity: 0,
        acc: new Float32Array(5), focus: !f.focus || (ev.c[k] >= 0 && !!f.focus[ev.c[k]]) });
    }
  }

  /** Keep places burning, let finished ones die down; the fighting on screen sets the ambience. */
  private tend(f: Frame) {
    let heat = 0, fire = 0;
    const idle = !f.playing && !f.replay;
    for (const [key, s] of this.sites) {
      const active = (s.long && f.t >= s.t0 && f.t < s.t1) || f.now < s.minUntil;
      const target = active ? 1 : 0;
      s.intensity += (target - s.intensity) * Math.min(1, f.dt * (active ? 2.5 : 0.7));
      if (!active && s.intensity < 0.03) {
        this.sites.delete(key);
        continue;
      }
      this.A.keep(s.anchor, f.now + 30);
      if (!this.A.on[s.anchor]) continue;
      const heavy = !!f.replay?.holding && (!f.focus || s.focus);
      burn(this.P, s.anchor, s.m, s.era, s.intensity * (idle ? 0.6 : 1), f.dt, f.quality, s.acc, heavy);
      heat += s.m * s.intensity;
      fire = Math.max(fire, s.m * s.intensity * (1 - hearing(this.A.sx[s.anchor], this.A.sy[s.anchor], f.view.w, f.view.h).dist));
    }
    this.level = 1 - Math.exp(-heat / 2.2);
    this.fire = fire;
  }

  /** Where on screen the fighting is, to be heard from: a burning place picked at random, the bigger and fiercer
   * the likelier; never one near an atrocity marked a moment ago. */
  place(view: View): { dist: number; pan: number } | null {
    const heard = [...this.sites.values()].filter((s) => this.A.on[s.anchor] &&
      !this.mourned(this.A.lon[s.anchor], this.A.lat[s.anchor], this.A.sx[s.anchor], this.A.sy[s.anchor]));
    let x = Math.random() * heard.reduce((sum, s) => sum + s.m * s.intensity, 0);
    for (const s of heard) {
      if ((x -= s.m * s.intensity) > 0) continue;
      return hearing(this.A.sx[s.anchor], this.A.sy[s.anchor], view.w, view.h);
    }
    return null;
  }

  private mourn(lon: number, lat: number, now: number) {
    this.mourning = this.mourning.filter((x) => x.until > now);
    const same = this.mourning.find((x) => km(x.lon, x.lat, lon, lat) < 30); // one place, many records: one zone
    if (same) same.until = now + MOURN;
    else {
      if (this.mourning.length >= 512) this.mourning.shift(); // the oldest give way; the newest is the one on screen
      this.mourning.push({ lon, lat, until: now + MOURN });
    }
  }

  /** Whether a place (lon, lat, and where it is on screen) is near an atrocity marked a moment ago: within MOURN_KM,
   * or close enough on screen to be heard as the same place when zoomed out. */
  private mourned(lon: number, lat: number, x: number, y: number): boolean {
    for (const z of this.mourning) {
      if (z.until <= this.clock) continue;
      if (km(z.lon, z.lat, lon, lat) < MOURN_KM) return true;
      const [zx, zy] = this.proj(z.lon + 360 * Math.round((this.centerLon - z.lon) / 360), z.lat);
      if (Math.hypot(zx - x, zy - y) < MOURN_PX) return true;
    }
    return false;
  }

  /** Whether the event with this id is drawn as an atrocity (a replay's chapter about one is a moment of quiet);
   * undefined while its period's events are not loaded. */
  isMemorial(id: string): boolean | undefined {
    for (const ev of this.sources) {
      const k = ev.id ? ev.id.indexOf(id) : -1;
      if (k >= 0) return ev.fx[k] === FX_MEMORIAL;
    }
    return undefined;
  }

  /** A chapter's battle, played big when the camera arrives. A chapter about a campaign (a conflict, not an event)
   * lights up that campaign's own best-known battles around the chapter's date, one after another. */
  hero(id: string, family: Uint8Array | null, t: number, delay: number, f: () => Frame | null): boolean {
    const at = this.clock + delay;
    const fire = (ev: FxEvents, k: number, when: number, boost: number, sound: boolean) => this.later(when, () => {
      const fr = f();
      if (!fr) return;
      const lon = ev.lon[k] + 360 * Math.round((fr.centerLon - ev.lon[k]) / 360);
      const [x, y] = fr.project(lon, ev.lat[k]);
      this.effect(ev, k, ev.fx[k] === FX_MEMORIAL ? ev.mag[k] : Math.max(boost, ev.mag[k]), fr, x, y, sound, true);
    });
    for (const ev of this.sources) {
      const k = ev.id ? ev.id.indexOf(id) : -1;
      if (k < 0 || ev.fx[k] === FX_NONE) continue;
      fire(ev, k, at, 0.8, true);
      return true;
    }
    if (!family) return false;
    const near: { ev: FxEvents; k: number; m: number }[] = [];
    for (const ev of this.sources) {
      if (ev.src !== "named") continue;
      const [lo, hi] = crossed(ev, t - 21 * DAY, t + 21 * DAY);
      for (let k = lo; k < hi; k++) if (ev.c[k] >= 0 && family[ev.c[k]] && ev.fx[k] !== FX_NONE) near.push({ ev, k, m: ev.mag[k] });
    }
    near.sort((a, b) => b.m - a.m);
    near.slice(0, 4).forEach((n, j) => fire(n.ev, n.k, at + j * 0.45, 0.7, j === 0));
    return near.length > 0;
  }
}
